import React from "react";
import {
    Alert,
    Box,
    Button,
    FormControl,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import type {SelectChangeEvent} from "@mui/material/Select";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TurnLeftIcon from "@mui/icons-material/TurnLeft";
import {useTranslation} from "react-i18next";
import PrintPreviewKeyValueTable, {PrintPreviewKeyValueRow} from "./PrintPreviewKeyValueTable";
import PrintPreviewVariablesSection from "./PrintPreviewVariablesSection";
import type {PrintPreviewVariableRow} from "./printoutDocumentVariables";

export interface PrintPreviewSidebarSelectOption {
    disabled?: boolean;
    group?: string;
    label: string;
    selectedLabel?: string;
    value: string;
}

export interface PrintPreviewSidebarSelect {
    disabled?: boolean;
    emptyLabel?: string;
    id: string;
    label: string;
    onChange: (value?: string) => void;
    options: PrintPreviewSidebarSelectOption[];
    value?: string;
}

export interface PrintPreviewSidebarTable {
    rows: PrintPreviewKeyValueRow[];
}

export interface PrintPreviewSidebarPrintoutInfo {
    language?: string;
    type?: string;
}

interface Props {
    alert?: React.ReactNode;
    closeLabel?: string;
    onClose: () => void;
    printoutInfo?: PrintPreviewSidebarPrintoutInfo;
    selects?: PrintPreviewSidebarSelect[];
    tables: PrintPreviewSidebarTable[];
    title: string;
    variables?: PrintPreviewVariableRow[];
}

const PrintPreviewSidebarContent = ({
                                        alert,
                                        closeLabel,
                                        onClose,
                                        printoutInfo,
                                        selects = [],
                                        tables,
                                        title,
                                        variables = [],
                                    }: Props) => {
    const {t} = useTranslation();

    const printoutInfoText = printoutInfo
        ? [printoutInfo.language, printoutInfo.type].filter(Boolean).join(" - ")
        : "";

    return (
        <Stack flex={1} spacing={2}>
            <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                spacing={2}
            >
                <Typography fontWeight={700} variant="h6">
                    {title}
                </Typography>
                <Button
                    color="error"
                    onClick={onClose}
                    startIcon={<CloseIcon/>}
                    variant="contained"
                >
                    {closeLabel ?? t("Close Print")}
                </Button>
            </Stack>

            {selects.map((select) => {
                const selectedValue = select.value && select.options.some(option => option.value === select.value)
                    ? select.value
                    : "";

                return (
                    <FormControl fullWidth key={select.id} size="small">
                        <InputLabel id={`${select.id}-label`}>{select.label}</InputLabel>
                        <Select
                            disabled={select.disabled || !select.options.length}
                            label={select.label}
                            labelId={`${select.id}-label`}
                            onChange={(event: SelectChangeEvent<string>) => (
                                select.onChange(event.target.value || undefined)
                            )}
                            renderValue={(value) => (
                                select.options.find(option => option.value === value)?.selectedLabel
                                ?? select.options.find(option => option.value === value)?.label
                                ?? value
                            )}
                            value={selectedValue}
                        >
                            {!select.options.length && (
                                <MenuItem disabled value="">
                                    {select.emptyLabel ?? t("No options")}
                                </MenuItem>
                            )}
                            {select.options.reduce<React.ReactNode[]>((items, option, index) => {
                                const previousGroup = select.options[index - 1]?.group;
                                const showGroupHeading = option.group && option.group !== previousGroup;

                                if (showGroupHeading) {
                                    items.push(
                                        <ListSubheader disableSticky key={`group-${option.group}`}>
                                            {option.group}
                                        </ListSubheader>,
                                    );
                                }

                                items.push(
                                    <MenuItem
                                        disabled={option.disabled}
                                        key={option.value || option.label}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>,
                                );

                                return items;
                            }, [])}
                        </Select>
                    </FormControl>
                );
            })}

            {printoutInfoText && (
                <Stack
                    alignItems="center"
                    direction="row"
                    spacing={0.75}
                    sx={{
                        color: "text.secondary",
                        minHeight: 18,
                        px: 0.5,
                    }}
                >
                    <TurnLeftIcon
                        sx={{
                            color: "text.disabled",
                            flexShrink: 0,
                            fontSize: 16,
                            transform: "rotate(180deg)",
                        }}
                    />
                    <Typography
                        component="div"
                        sx={{
                            fontSize: 12,
                            lineHeight: "18px",
                            wordBreak: "break-word",
                        }}
                    >
                        {printoutInfoText}
                    </Typography>
                </Stack>
            )}

            {/*
                The only way from a print preview to the printouts themselves. It opens a new tab
                because this page is opened from a reservation or a folio and is the only place that
                context lives - navigating this tab to settings would throw it away.
            */}
            <Box>
                <Button
                    size="small"
                    color="inherit"
                    component="a"
                    href="/system/printouts"
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon sx={{fontSize: 14}}/>}
                    sx={{color: "text.secondary", textTransform: "none", px: 0.5}}
                >
                    {t("Manage printouts")}
                </Button>
            </Box>

            {/* Keep print details near the action on desktop. */}
            <Box
                flexGrow={{
                    xs: 0,
                    md: 1,
                }}
                minHeight={{
                    xs: 12,
                    md: 48,
                }}
            />

            <Stack spacing={2}>
                {alert && (
                    <Alert severity="info" sx={{py: 0.5}}>
                        {alert}
                    </Alert>
                )}

                {tables.map((table, index) => (
                    <PrintPreviewKeyValueTable
                        key={index}
                        rows={table.rows}
                    />
                ))}

                <PrintPreviewVariablesSection rows={variables}/>
            </Stack>
        </Stack>
    );
};

export default PrintPreviewSidebarContent;
